import {
	ActionIcon,
	Box,
	Button,
	Group,
	NumberInput,
	Select,
	Stack,
	Tabs,
	Textarea,
	TextInput,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { ExerciseTypeEnum } from '@codezo/graphql';
import { MultilingualMediaUploader } from '../../../components/MultilingualMediaUploader';

interface ExerciseTypeFieldsProps {
	form: UseFormReturnType<any>;
	exerciseIndex: number;
	exerciseType: string;
	languages: readonly string[];
	languageLabels: Record<string, string>;
}

export function ExerciseTypeFields({ form, exerciseIndex, exerciseType, languages, languageLabels }: ExerciseTypeFieldsProps) {
	const [activeTab, setActiveTab] = useState<string>('en');

	// Get the active language index
	const activeLangIndex = form.values.exercises[exerciseIndex]?.translations?.findIndex(
		(t: any) => t.language === activeTab
	);

	// Helper to check if a language tab has errors
	const hasLanguageErrors = (lang: string) => {
		const langIndex = form.values.exercises[exerciseIndex]?.translations?.findIndex(
			(t: any) => t.language === lang
		);
		if (langIndex === -1) return false;

		const exerciseErrors = (form.errors?.exercises as any)?.[exerciseIndex];
		if (!exerciseErrors) return false;

		// Check translation errors (name, description, tips)
		const translationErrors = (exerciseErrors.translations as any)?.[langIndex];
		if (translationErrors && Object.keys(translationErrors).length > 0) return true;

		// Check reflection input errors for this language
		if (exerciseType === ExerciseTypeEnum.ReflectionInput) {
			const inputs = form.values.exercises[exerciseIndex]?.typeData?.inputs || [];
			for (let inputIdx = 0; inputIdx < inputs.length; inputIdx++) {
				const inputError = (exerciseErrors.typeData?.inputs as any)?.[inputIdx]?.translations?.[langIndex];
				if (inputError && Object.keys(inputError).length > 0) return true;
			}
		}

		return false;
	};

	// Helper to get error for a specific field in the active language
	const getFieldError = (field: string) => {
		const exerciseErrors = (form.errors?.exercises as any)?.[exerciseIndex];
		if (!exerciseErrors) return '';

		const translationErrors = (exerciseErrors.translations as any)?.[activeLangIndex];
		return translationErrors?.[field] || '';
	};

	// Helper to get error for a tip
	const getTipError = (tipIndex: number) => {
		const exerciseErrors = (form.errors?.exercises as any)?.[exerciseIndex];
		if (!exerciseErrors) return '';

		const translationErrors = (exerciseErrors.translations as any)?.[activeLangIndex];
		return translationErrors?.tips?.[tipIndex] || '';
	};

	// Calculate max group ID to enable auto-expanding dropdown
	const maxGroupId = form.values.exercises.reduce((max: number, ex: any) => {
		if (ex.groupId !== null && ex.groupId !== undefined && ex.groupId > max) {
			return ex.groupId;
		}
		return max;
	}, 0);

	// Create group options: None, 1, 2, ..., maxGroupId, maxGroupId+1
	const groupOptions = [
		{ value: 'none', label: 'No Group (Standalone Exercise)' },
		...Array.from({ length: maxGroupId + 1 }, (_, i) => ({
			value: String(i + 1),
			label: `Group ${i + 1}`,
		})),
	];

	// Get current group value for Select
	const currentExercise = form.values.exercises[exerciseIndex];
	const currentGroupValue =
		currentExercise?.groupId !== null && currentExercise?.groupId !== undefined
			? String(currentExercise.groupId)
			: 'none';

	return (
		<Stack gap="md">
			{/* Translation fields for name and tips */}
			<Tabs value={activeTab} onChange={(value) => setActiveTab(value || languages[0])}>
				<Tabs.List>
					{languages.map((lang) => (
						<Tabs.Tab
							key={lang}
							value={lang}
							color={hasLanguageErrors(lang) ? 'red' : undefined}
							style={{ color: hasLanguageErrors(lang) ? 'var(--mantine-color-red-6)' : undefined }}
						>
							{languageLabels[lang]}
						</Tabs.Tab>
					))}
				</Tabs.List>

				{/* Only render the active tab's content */}
				{activeLangIndex !== -1 && (
					<Tabs.Panel value={activeTab} pt="md">
						<Stack gap="md">
							<Textarea
								label="Exercise Name"
								placeholder={`Name in ${languageLabels[activeTab]}`}
								description="Use bullet points (• or -) for lists. Each line will be rendered separately."
								required={activeTab === 'en'}
								rows={3}
								autosize
								minRows={2}
								maxRows={6}
								value={form.values.exercises[exerciseIndex].translations[activeLangIndex]?.name || ''}
								onChange={(e) => form.setFieldValue(
									`exercises.${exerciseIndex}.translations.${activeLangIndex}.name`,
									e.target.value
								)}
								error={getFieldError('name')}
							/>

							{/* Hide description for REFLECTION_INPUT exercises */}
							{exerciseType !== ExerciseTypeEnum.ReflectionInput && (
								<Textarea
									label="Description"
									placeholder={`Description in ${languageLabels[activeTab]}`}
									rows={3}
									value={form.values.exercises[exerciseIndex].translations[activeLangIndex]?.description || ''}
									onChange={(e) => form.setFieldValue(
										`exercises.${exerciseIndex}.translations.${activeLangIndex}.description`,
										e.target.value
									)}
									error={getFieldError('description')}
								/>
							)}

							{/* Tips */}
							<Box>
								<Group justify="space-between" mb="xs">
									<Box fw={500} size="sm">
										Tips
									</Box>
									<Button
										size="xs"
										variant="subtle"
										leftSection={<IconPlus size={14} />}
										onClick={() => {
											// Add empty tip to ALL languages to keep them in sync
											form.values.exercises[exerciseIndex].translations.forEach(
												(_: any, langIndex: number) => {
													const currentTips =
														form.values.exercises[exerciseIndex].translations[langIndex]
															.tips || [];
													form.setFieldValue(
														`exercises.${exerciseIndex}.translations.${langIndex}.tips`,
														[...currentTips, '']
													);
												}
											);
										}}
									>
										Add Tip
									</Button>
								</Group>

								{form.values.exercises[exerciseIndex].translations[
									activeLangIndex
								].tips?.map((_: any, tipIndex: number) => (
									<Group key={tipIndex} mb="xs" align="flex-start">
										<Textarea
											placeholder={`Tip ${tipIndex + 1}`}
											style={{ flex: 1 }}
											value={form.values.exercises[exerciseIndex].translations[activeLangIndex].tips[tipIndex] || ''}
											onChange={(e) => form.setFieldValue(
												`exercises.${exerciseIndex}.translations.${activeLangIndex}.tips.${tipIndex}`,
												e.target.value
											)}
											error={getTipError(tipIndex)}
										/>
										<ActionIcon
											color="red"
											variant="subtle"
											onClick={() => {
												// Remove tip at this index from ALL languages to keep them in sync
												form.values.exercises[exerciseIndex].translations.forEach(
													(_: any, langIndex: number) => {
														const currentTips =
															form.values.exercises[exerciseIndex].translations[langIndex]
																.tips || [];
														form.setFieldValue(
															`exercises.${exerciseIndex}.translations.${langIndex}.tips`,
															currentTips.filter((__: any, i: number) => i !== tipIndex)
														);
													}
												);
											}}
										>
											<IconTrash size={16} />
										</ActionIcon>
									</Group>
								))}
							</Box>
						</Stack>
					</Tabs.Panel>
				)}
			</Tabs>

			{/* Exercise Group Selection */}
			<Select
				label="Exercise Group"
				description="Group exercises as alternatives. Users must answer at least one exercise from each group."
				placeholder="Select a group"
				data={groupOptions}
				value={currentGroupValue}
				onChange={(value) => {
					const groupId = value === 'none' ? null : parseInt(value || '0', 10);
					form.setFieldValue(`exercises.${exerciseIndex}.groupId`, groupId);
				}}
				clearable
			/>

			{/* Duration range fields (optional for non-media types) */}
			{exerciseType !== ExerciseTypeEnum.Audio && exerciseType !== ExerciseTypeEnum.Video && (
				<Group grow>
					<NumberInput
						label="Min Duration (seconds)"
						placeholder="Optional"
						min={0}
						{...form.getInputProps(`exercises.${exerciseIndex}.durationMin`)}
					/>
					<NumberInput
						label="Max Duration (seconds)"
						placeholder="Optional"
						min={0}
						{...form.getInputProps(`exercises.${exerciseIndex}.durationMax`)}
					/>
				</Group>
			)}

			{/* Type-specific fields */}
			{exerciseType === ExerciseTypeEnum.ReflectionInput && (
				<ReflectionInputFields form={form} exerciseIndex={exerciseIndex} activeTab={activeTab} activeLangIndex={activeLangIndex} languages={languages} languageLabels={languageLabels} />
			)}

			{(exerciseType === ExerciseTypeEnum.Audio || exerciseType === ExerciseTypeEnum.Video) && (
				<MediaFields form={form} exerciseIndex={exerciseIndex} type={exerciseType} languages={languages} languageLabels={languageLabels} />
			)}

			{exerciseType === ExerciseTypeEnum.Breathing && (
				<BreathingFields form={form} exerciseIndex={exerciseIndex} />
			)}

			{exerciseType === ExerciseTypeEnum.ImageUpload && (
				<ImageUploadFields form={form} exerciseIndex={exerciseIndex} />
			)}
		</Stack>
	);
}

function ReflectionInputFields({
	form,
	exerciseIndex,
	activeTab,
	activeLangIndex,
	languages,
	languageLabels,
}: {
	form: UseFormReturnType<any>;
	exerciseIndex: number;
	activeTab: string;
	activeLangIndex: number;
	languages: readonly string[];
	languageLabels: Record<string, string>;
}) {
	const addInput = () => {
		const currentInputs = form.values.exercises[exerciseIndex].typeData.inputs || [];
		form.setFieldValue(`exercises.${exerciseIndex}.typeData.inputs`, [
			...currentInputs,
			{
				translations: languages.map(lang => ({
					language: lang,
					title: '',
					placeholder: ''
				})),
			},
		]);
	};

	const removeInput = (inputIndex: number) => {
		const currentInputs = form.values.exercises[exerciseIndex].typeData.inputs || [];
		form.setFieldValue(
			`exercises.${exerciseIndex}.typeData.inputs`,
			currentInputs.filter((_: any, i: number) => i !== inputIndex)
		);
	};

	return (
		<Stack gap="md">
			<Group justify="space-between">
				<Box fw={500}>Reflection Inputs</Box>
				<Button
					size="xs"
					variant="subtle"
					leftSection={<IconPlus size={14} />}
					onClick={addInput}
				>
					Add Input
				</Button>
			</Group>

			{form.values.exercises[exerciseIndex].typeData.inputs?.map(
				(_: any, inputIndex: number) => {
					// Get the active language index for this input
					const inputLangIndex = form.values.exercises[
						exerciseIndex
					].typeData.inputs[inputIndex]?.translations?.findIndex(
						(t: any) => t.language === activeTab
					);

					// Helper to get error for reflection input fields
					const getInputFieldError = (field: string) => {
						const exerciseErrors = (form.errors?.exercises as any)?.[exerciseIndex];
						if (!exerciseErrors) return '';

						const inputError = (exerciseErrors.typeData?.inputs as any)?.[inputIndex]?.translations?.[activeLangIndex];
						return inputError?.[field] || '';
					};

					return (
						<Box
							key={inputIndex}
							p="sm"
							style={{ border: '1px solid #dee2e6', borderRadius: '4px' }}
						>
							<Group justify="space-between" mb="xs">
								<Box size="sm" fw={500}>
									Input {inputIndex + 1}
								</Box>
								<ActionIcon
									color="red"
									variant="subtle"
									size="sm"
									onClick={() => removeInput(inputIndex)}
								>
									<IconTrash size={14} />
								</ActionIcon>
							</Group>

							<Stack gap="xs">
								{/* Hide Title field - not used in current implementation */}
								{/* <TextInput
									label="Title"
									placeholder={`e.g., 'What challenged you today?'`}
									value={form.values.exercises[exerciseIndex].typeData.inputs[inputIndex].translations[inputLangIndex]?.title || ''}
									onChange={(e) => form.setFieldValue(
										`exercises.${exerciseIndex}.typeData.inputs.${inputIndex}.translations.${inputLangIndex}.title`,
										e.target.value
									)}
									error={getInputFieldError('title')}
								/> */}
								<TextInput
									label="Placeholder"
									placeholder={`e.g., 'Describe a specific situation...'`}
									value={form.values.exercises[exerciseIndex].typeData.inputs[inputIndex].translations[inputLangIndex]?.placeholder || ''}
									onChange={(e) => form.setFieldValue(
										`exercises.${exerciseIndex}.typeData.inputs.${inputIndex}.translations.${inputLangIndex}.placeholder`,
										e.target.value
									)}
									error={getInputFieldError('placeholder')}
								/>
							</Stack>
						</Box>
					);
				}
			)}
		</Stack>
	);
}

function MediaFields({
	form,
	exerciseIndex,
	type,
	languages,
	languageLabels,
}: {
	form: UseFormReturnType<any>;
	exerciseIndex: number;
	type: string;
	languages: readonly string[];
	languageLabels: Record<string, string>;
}) {
	const currentValue = form.values.exercises[exerciseIndex]?.typeData;

	console.log('[MediaFields] Exercise typeData:', currentValue);
	console.log('[MediaFields] Has mediaUrls?', currentValue?.mediaUrls?.length);

	return (
		<Stack gap="md">
			<MultilingualMediaUploader
				exerciseType={type as 'AUDIO' | 'VIDEO'}
				languages={languages}
				languageLabels={languageLabels}
				value={
					currentValue?.mediaUrls?.length > 0
						? {
								duration: currentValue.duration || 0,
								mediaUrls: currentValue.mediaUrls,
							}
						: undefined
				}
				onChange={(value) => {
					if (value) {
						form.setFieldValue(`exercises.${exerciseIndex}.typeData`, {
							url: value.mediaUrls[0]?.url || null, // Deprecated: for backwards compatibility
							duration: value.duration,
							fileId: value.mediaUrls[0]?.fileId || null, // Deprecated: for backwards compatibility
							mediaUrls: value.mediaUrls,
						});
						// Also update the duration range fields (use duration as min)
						form.setFieldValue(`exercises.${exerciseIndex}.durationMin`, value.duration);
						form.setFieldValue(`exercises.${exerciseIndex}.durationMax`, null);
					} else {
						form.setFieldValue(`exercises.${exerciseIndex}.typeData`, {
							url: null,
							duration: 0,
							fileId: null,
							mediaUrls: [],
						});
						form.setFieldValue(`exercises.${exerciseIndex}.durationMin`, null);
						form.setFieldValue(`exercises.${exerciseIndex}.durationMax`, null);
					}
				}}
				onFileIdChange={(fileId) => {
					// This will be used by parent form for cleanup tracking
					const currentExercise = form.values.exercises[exerciseIndex];
					if (currentExercise) {
						currentExercise._uploadedFileId = fileId;
					}
				}}
			/>
			<NumberInput
				label="Duration (seconds)"
				description="Override if the auto-detected duration is incorrect"
				placeholder="Duration in seconds"
				min={0}
				value={currentValue?.duration || 0}
				onChange={(value) => {
					const duration = typeof value === 'number' ? value : 0;
					form.setFieldValue(`exercises.${exerciseIndex}.typeData.duration`, duration);
					form.setFieldValue(`exercises.${exerciseIndex}.durationMin`, duration || null);
				}}
			/>
		</Stack>
	);
}

function BreathingFields({
	form,
	exerciseIndex,
}: {
	form: UseFormReturnType<any>;
	exerciseIndex: number;
}) {
	return (
		<Stack gap="md">
			<Group grow>
				<NumberInput
					label="Inhale (seconds)"
					min={1}
					required
					{...form.getInputProps(`exercises.${exerciseIndex}.typeData.inhale`)}
				/>
				<NumberInput
					label="Inhale Hold (seconds)"
					min={0}
					required
					{...form.getInputProps(`exercises.${exerciseIndex}.typeData.inhaleHold`)}
				/>
			</Group>
			<Group grow>
				<NumberInput
					label="Exhale (seconds)"
					min={1}
					required
					{...form.getInputProps(`exercises.${exerciseIndex}.typeData.exhale`)}
				/>
				<NumberInput
					label="Exhale Hold (seconds)"
					min={0}
					required
					{...form.getInputProps(`exercises.${exerciseIndex}.typeData.exhaleHold`)}
				/>
			</Group>
		</Stack>
	);
}

function ImageUploadFields({ form, exerciseIndex }: { form: UseFormReturnType<any>; exerciseIndex: number }) {
	return (
		<Stack gap="md">
			<Group grow>
				<NumberInput
					label="Minimum Images"
					description="Minimum number of images the user must upload"
					min={0}
					max={20}
					required
					{...form.getInputProps(`exercises.${exerciseIndex}.typeData.minImages`)}
				/>
				<NumberInput
					label="Maximum Images"
					description="Maximum number of images the user can upload"
					min={1}
					max={20}
					required
					{...form.getInputProps(`exercises.${exerciseIndex}.typeData.maxImages`)}
				/>
			</Group>
		</Stack>
	);
}
